import React, { useState, useRef, useEffect, useMemo } from 'react';
import styles from './channel-dashboard.module.scss';
import { getChannelArtwork, DEFAULT_ALBUM_ART } from '@/utils/artwork';
import { Container, Card, Button, Row as BootstrapRow, Col as BootstrapCol } from 'react-bootstrap';
import PageTitle from '@/common/PageTitle/PageTitle';
import { Form, Button as AntdButton, Select, Spin } from 'antd';
import { FilterOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import FormItem from '@/common/FormItem/FormItem';
import DateRangePicker from '@/common/DateRangePicker/DateRangePicker';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import type { Channel as ApiChannel } from '@/features/channels/types';
import {
  useListChannelsQuery,
  useCreateChannelMutation,
  useUpdateChannelMutation,
  useGetChannelArtworkUploadUrlMutation,
  extractChannelErrorMessage,
} from '@/features/channels/channelsApi';
import {
  toUiStatus,
  glanceCounts,
  formatChannelUpdatedAt,
  type UiChannelStatus,
} from '@/features/channels/channelStatus';

interface Channel {
  id: string;
  name: string;
  description: string;
  status: UiChannelStatus;
  liveSongs: number;
  liveRotations: number;
  songs: number;
  rotations: number;
  lastUpdated: string;
  artwork: string;
  featuredArtists: string;
  genres: string;
  energyLevel: string;
}

function toDashboardRow(ch: ApiChannel): Channel {
  const counts = glanceCounts(ch);
  return {
    id: ch.channelId,
    name: ch.name,
    description: ch.description || '',
    status: toUiStatus(ch.status),
    liveSongs: ch.productionSongCount ?? 0,
    liveRotations: ch.productionRotationCount ?? 0,
    songs: counts.songs,
    rotations: counts.rotations,
    lastUpdated: formatChannelUpdatedAt(ch.updatedAt),
    artwork: ch.artworkUrl || DEFAULT_ALBUM_ART,
    featuredArtists: ch.featuredArtists || '',
    genres: ch.genres || '',
    energyLevel: ch.energyLevel || 'Medium to high',
  };
}

const ChannelDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isFetching, error: listError } = useListChannelsQuery();
  const [createChannel, createState] = useCreateChannelMutation();
  const [updateChannel, updateState] = useUpdateChannelMutation();
  const [getArtworkUploadUrl, artworkUploadState] = useGetChannelArtworkUploadUrlMutation();

  const apiChannels = useMemo(
    () => (data?.items || []).map(toDashboardRow),
    [data],
  );

  const [sortDirection, setSortDirection] = useState<'none' | 'asc' | 'desc'>('none');
  const [sortedChannels, setSortedChannels] = useState<Channel[]>([]);
  const [editingChannelId, setEditingChannelId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<string | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(undefined);
  const [resetCounter, setResetCounter] = useState(0);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  const [channelEnergyLevels, setChannelEnergyLevels] = useState<Record<string, string>>({});
  const editableContainerRef = useRef<HTMLDivElement>(null);
  const artworkFileInputRef = useRef<HTMLInputElement>(null);
  const [genresInput, setGenresInput] = useState<Record<string, string>>({});
  const [featuredArtistsInput, setFeaturedArtistsInput] = useState<Record<string, string>>({});
  const [uploadingArtworkFor, setUploadingArtworkFor] = useState<string | null>(null);

  useEffect(() => {
    if (listError) toast.error(extractChannelErrorMessage(listError));
  }, [listError]);

  useEffect(() => {
    setChannelEnergyLevels((prev) => {
      const next = { ...prev };
      for (const ch of apiChannels) {
        if (next[ch.id] === undefined) {
          next[ch.id] = ch.energyLevel || 'Medium to high';
        }
      }
      return next;
    });
    setGenresInput((prev) => {
      const next = { ...prev };
      for (const ch of apiChannels) {
        if (next[ch.id] === undefined) next[ch.id] = ch.genres;
      }
      return next;
    });
    setFeaturedArtistsInput((prev) => {
      const next = { ...prev };
      for (const ch of apiChannels) {
        if (next[ch.id] === undefined) next[ch.id] = ch.featuredArtists;
      }
      return next;
    });
  }, [apiChannels]);

  const applyClientFilters = (
    source: Channel[],
    channelName?: string,
    status?: string,
    range?: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null,
    direction: 'none' | 'asc' | 'desc' = sortDirection,
  ) => {
    let filtered = [...source];
    if (channelName) {
      filtered = filtered.filter((channel) => channel.name === channelName);
    }
    if (status) {
      filtered = filtered.filter((channel) => channel.status === status);
    }
    if (range && range[0] && range[1]) {
      const startDateStr = range[0].format('YYYY-MM-DD');
      const endDateStr = range[1].format('YYYY-MM-DD');
      filtered = filtered.filter((channel) => {
        const channelDate = new Date(channel.lastUpdated);
        const filterStartDate = new Date(startDateStr);
        const filterEndDate = new Date(endDateStr);
        return channelDate >= filterStartDate && channelDate <= filterEndDate;
      });
    }
    if (direction === 'asc') {
      filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    } else if (direction === 'desc') {
      filtered = [...filtered].sort((a, b) => b.name.localeCompare(a.name));
    }
    return filtered;
  };

  useEffect(() => {
    setSortedChannels(
      applyClientFilters(apiChannels, selectedChannel, selectedStatus, dateRange, sortDirection),
    );
  }, [apiChannels, selectedChannel, selectedStatus, dateRange, sortDirection]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (uploadingArtworkFor) return;
      if (editingChannelId &&
          editableContainerRef.current &&
          !editableContainerRef.current.contains(event.target as Node) &&
          !(event.target as Element).closest('a[href="#edit"]')) {
        void handleSaveAndClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingChannelId, sortedChannels, channelEnergyLevels, genresInput, featuredArtistsInput, uploadingArtworkFor, apiChannels]);

  const handleSaveAndClose = async () => {
    if (!editingChannelId) return;
    const channel = sortedChannels.find((c) => c.id === editingChannelId);
    if (!channel) {
      setEditingChannelId(null);
      setDragOver(false);
      return;
    }

    const server = apiChannels.find((c) => c.id === editingChannelId);
    const nextName = channel.name;
    const nextDescription = channel.description;
    const nextGenres = genresInput[editingChannelId] !== undefined
      ? genresInput[editingChannelId]
      : channel.genres;
    const nextFeaturedArtists = featuredArtistsInput[editingChannelId] !== undefined
      ? featuredArtistsInput[editingChannelId]
      : channel.featuredArtists;
    const nextEnergy = channelEnergyLevels[editingChannelId] || channel.energyLevel;

    const norm = (v: string | null | undefined) => (v == null || v === '' ? null : v);
    const patch: {
      channelId: string;
      name?: string;
      description?: string;
      genres?: string | null;
      featuredArtists?: string | null;
      energyLevel?: string | null;
    } = { channelId: editingChannelId };

    if (server) {
      if (nextName !== server.name) patch.name = nextName;
      if (nextDescription !== server.description) patch.description = nextDescription;
      if (norm(nextGenres) !== norm(server.genres)) patch.genres = nextGenres;
      if (norm(nextFeaturedArtists) !== norm(server.featuredArtists)) {
        patch.featuredArtists = nextFeaturedArtists;
      }
      if (nextEnergy !== server.energyLevel) patch.energyLevel = nextEnergy;
    } else {
      // Fallback if the row isn't in the filtered list — send full snapshot.
      patch.name = nextName;
      patch.description = nextDescription;
      patch.genres = nextGenres;
      patch.featuredArtists = nextFeaturedArtists;
      patch.energyLevel = nextEnergy;
    }

    const hasMutableField = Object.keys(patch).some((k) => k !== 'channelId');
    if (!hasMutableField) {
      // e.g. artwork was already persisted by upload; nothing else changed.
      setEditingChannelId(null);
      setDragOver(false);
      return;
    }

    try {
      await updateChannel(patch).unwrap();
      toast.success('Channel updated');
    } catch (err) {
      toast.error(extractChannelErrorMessage(err));
    }

    setEditingChannelId(null);
    setDragOver(false);
  };

  const handleSort = (direction: 'none' | 'asc' | 'desc') => {
    setSortDirection(direction);
  };

  const handleEditClick = (e: React.MouseEvent, channelId: string) => {
    e.preventDefault();
    if (editingChannelId === channelId) {
      void handleSaveAndClose();
    } else {
      setEditingChannelId(channelId);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const uploadArtworkFile = async (channelId: string, file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Choose an image file (JPEG, PNG, WebP, or GIF)');
      return;
    }

    setUploadingArtworkFor(channelId);
    try {
      const { uploadUrl, key } = await getArtworkUploadUrl({
        channelId,
        contentType: file.type,
      }).unwrap();

      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!putRes.ok) {
        throw new Error(`S3 upload failed (HTTP ${putRes.status})`);
      }

      const updated = await updateChannel({
        channelId,
        artworkKey: key,
      }).unwrap();

      setSortedChannels((prev) =>
        prev.map((channel) =>
          channel.id === channelId
            ? { ...channel, artwork: updated.artworkUrl || DEFAULT_ALBUM_ART }
            : channel,
        ),
      );
      toast.success('Channel artwork saved');
    } catch (err) {
      toast.error(extractChannelErrorMessage(err));
    } finally {
      setUploadingArtworkFor(null);
    }
  };

  const handleDrop = async (e: React.DragEvent, channelId: string) => {
    e.preventDefault();
    setDragOver(false);

    const file = e.dataTransfer.files[0];
    if (!file) {
      toast.error('Drop an image file (JPEG, PNG, WebP, or GIF)');
      return;
    }
    await uploadArtworkFile(channelId, file);
  };

  const handleArtworkClick = (channelId: string) => {
    if (uploadingArtworkFor) return;
    const input = artworkFileInputRef.current;
    if (!input) return;
    input.dataset.channelId = channelId;
    input.value = '';
    input.click();
  };

  const handleArtworkFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const channelId = e.target.dataset.channelId;
    e.target.value = '';
    if (!file || !channelId) return;
    await uploadArtworkFile(channelId, file);
  };

  const handleChannelSelect = (value: string | undefined) => {
    setSelectedChannel(value);
  };

  const handleReset = () => {
    setSelectedChannel(undefined);
    setSelectedStatus(undefined);
    setDateRange(null);
    setSortDirection('none');
    setResetCounter(prev => prev + 1);
  };

  const handleDateChange = (dates: any) => {
    setDateRange(dates);
  };

  const channelOptions = Array.from(new Set(apiChannels.map(channel => channel.name))).map(name => ({ label: name, value: name }));
  const statusOptions = Array.from(new Set(apiChannels.map(channel => channel.status))).map(status => ({ label: status, value: status }));

  const handleEnergyLevelChange = (e: React.ChangeEvent<HTMLSelectElement>, channelId: string) => {
    setChannelEnergyLevels(prev => ({
      ...prev,
      [channelId]: e.target.value
    }));
  };

  const handleStatusSelect = (value: string | undefined) => {
    setSelectedStatus(value);
  };

  const handleAddNewChannel = async () => {
    try {
      const created = await createChannel({
        name: 'New Channel',
        status: 'PENDING',
        order: apiChannels.length + 1,
        channelType: 'Commercial',
      }).unwrap();
      toast.success(`Created channel "${created.name}"`);
    } catch (err) {
      toast.error(extractChannelErrorMessage(err));
    }
  };

  return (
      <div className={styles.root}>
      <Container fluid>
      <Form
        requiredMark={false}
        layout="vertical"
        initialValues={{
          channels: null,
          status: null,
        }}
      >
        <Card>
          <Card.Body>
            <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
              <PageTitle title="Channel Dashboard" />
              <Button
                onClick={handleReset}
                style={{
                  background: 'white',
                  color: '#333',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontWeight: '500'
                }}
              >
                <FilterOutlined /> Reset All
              </Button>
            </Card.Title>
            <BootstrapRow className="mb-2">
              <BootstrapCol>
                <BootstrapRow>
                  <BootstrapCol md={4}>
                    {/*
                      CRITICAL: Do NOT add 'name' prop to FormItem when using external state management!
                      The 'name' prop creates Ant Design form state that conflicts with our React state.
                      This causes Select components to not clear properly on reset.

                      ✅ CORRECT: <FormItem label="Channel"> (no name prop)
                      ❌ WRONG:   <FormItem name="channel" label="Channel">

                      This pattern works for all Ant Design Select components with external state management.
                      See: https://ant.design/components/form#formitem for more details.
                    */}
                    <FormItem
                      label="Channel"
                      className="customSelectWrapper"
                      layout="vertical"
                    >
                      <Select
                        key={`channels-reset-${resetCounter}`}
                        showSearch
                        allowClear
                        className="customSelect"
                        placeholder="Any channel"
                        value={selectedChannel}
                        onChange={handleChannelSelect}
                        options={channelOptions}
                      />
                    </FormItem>
                  </BootstrapCol>
                  <BootstrapCol md={4}>
                    <FormItem
                      label="Channel Status"
                      className="customSelectWrapper"
                      layout="vertical"
                    >
                      <Select
                        key={`status-reset-${resetCounter}`}
                        showSearch
                        allowClear
                        className="customSelect"
                        placeholder="Any status"
                        value={selectedStatus}
                        onChange={handleStatusSelect}
                        options={statusOptions}
                      />
                    </FormItem>
                  </BootstrapCol>
                </BootstrapRow>
              </BootstrapCol>
            </BootstrapRow>
            <BootstrapRow className="mb-2">
              <BootstrapCol md={8}>
                <FormItem
                  label="Last Updated"
                  layout="vertical"
                >
                      <DateRangePicker
                        key={`dateRange-reset-${resetCounter}`}
                        maxDate={dayjs()}
                        value={dateRange}
                        onChange={handleDateChange}
                        style={{ width: '100%' }}
                      />
                </FormItem>
              </BootstrapCol>
              <BootstrapCol
                md={4}
                className="d-flex align-items-center"
                style={{ marginTop: '5px' }}
              >
                <AntdButton
                  className="w-100"
                  style={{
                    background: 'white',
                    color: '#e21e26',
                    border: '1px solid #e21e26',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    justifyContent: 'center',
                    height: '50px'
                  }}
                  onClick={handleAddNewChannel}
                  loading={createState.isLoading}
                >
                  <PlusOutlined style={{ fontSize: 16 }} />
                  Add New Channel
                </AntdButton>
              </BootstrapCol>
            </BootstrapRow>
            <Spin spinning={isLoading || isFetching || updateState.isLoading || artworkUploadState.isLoading || !!uploadingArtworkFor} indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}>
            <div className={styles.tableContainer} ref={editableContainerRef}>
            <input
              ref={artworkFileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              style={{ display: 'none' }}
              onChange={(e) => { void handleArtworkFileChange(e); }}
            />
            <table className={styles.channelDashboardTable}>
              <thead>
                <tr>
                  <th className={styles.artworkCol}>Artwork</th>
                  <th>
                    <div className={styles.sortHeader}>
                      <span className={styles.sortIcons}>
                        <span
                          className={`${styles.sortUp} ${sortDirection === 'asc' ? styles.active : ''}`}
                          onClick={() => handleSort('asc')}
                        >
                          ▲
                        </span>
                        <span
                          className={`${styles.sortDown} ${sortDirection === 'desc' ? styles.active : ''}`}
                          onClick={() => handleSort('desc')}
                        >
                          ▼
                        </span>
                      </span>
                      Channel
                    </div>
                  </th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Songs</th>
                  <th>Rotations</th>
                  <th>Updated</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedChannels.map((channel, idx) => (
                  <React.Fragment key={channel.id}>
                    <tr>
                      <td className={styles.artworkCell}>
                        {editingChannelId === channel.id ? (
                          <div
                            className={`${styles.artworkUpload} ${dragOver ? styles.dragOver : ''}`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => { void handleDrop(e, channel.id); }}
                            onClick={() => handleArtworkClick(channel.id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleArtworkClick(channel.id);
                              }
                            }}
                            aria-label={`Upload artwork for ${channel.name}`}
                            style={{ cursor: uploadingArtworkFor === channel.id ? 'wait' : 'pointer' }}
                          >
                            <img src={getChannelArtwork(channel.artwork)} alt={channel.name} className={styles.artwork} />
                            <div className={styles.uploadOverlay}>
                              <span>
                                {uploadingArtworkFor === channel.id
                                  ? 'Uploading…'
                                  : 'Drag & Drop or Click'}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <img src={getChannelArtwork(channel.artwork)} alt={channel.name} className={styles.artwork} />
                        )}
                      </td>
                      <td>
                        {editingChannelId === channel.id ? (
                          <input
                            className={styles.channelNameInput}
                            type="text"
                            value={channel.name}
                            style={{ width: 90, height: 20, minHeight: 20, lineHeight: '20px', fontSize: 12, paddingTop: 0, paddingBottom: 0, boxSizing: 'border-box' }}
                            onChange={e => {
                              const value = e.target.value;
                              setSortedChannels(prev => prev.map(c => c.id === channel.id ? { ...c, name: value } : c));
                            }}
                          />
                        ) : (
                          <div className={styles.channelName}>{channel.name}</div>
                        )}
                        <div className={styles.subheadings}>
                          <div className={`${styles.channelSubheading} ${styles.energyLevelSubheading}`}>Energy Level</div>
                        </div>
                        {editingChannelId === channel.id ? (
                          <select
                            className={styles.energySelect}
                            value={channelEnergyLevels[channel.id]}
                            onChange={(e) => handleEnergyLevelChange(e, channel.id)}
                          >
                            <option value="High">High</option>
                            <option value="Medium to high">Medium to high</option>
                            <option value="Medium">Medium</option>
                            <option value="Medium to low">Medium to low</option>
                            <option value="Low">Low</option>
                          </select>
                        ) : (
                          <div className={styles.energyValue}>{channelEnergyLevels[channel.id]}</div>
                        )}
                      </td>
                      <td>
                        {editingChannelId === channel.id ? (
                          <textarea
                            value={channel.description}
                            style={{ width: '100%', minHeight: 32, marginBottom: 8, boxSizing: 'border-box' }}
                            onChange={e => {
                              const value = e.target.value;
                              setSortedChannels(prev => prev.map(c => c.id === channel.id ? { ...c, description: value } : c));
                            }}
                          />
                        ) : (
                          <div className={`description-text genres-box`}>{channel.description}</div>
                        )}
                      </td>
                      <td>{channel.status}</td>
                      <td>{channel.songs}</td>
                      <td>{channel.rotations}</td>
                      <td>{channel.lastUpdated}</td>
                      <td>
                        <div>
                          <a className={styles.actionLink} href="#edit" onClick={e => handleEditClick(e, channel.id)}>Edit</a> | <a className={styles.actionLink} href="#program" onClick={() => navigate(`/channel-programming/${channel.id}`)}>Program</a>
                        </div>
                      </td>
                    </tr>
                    {idx !== sortedChannels.length - 1 && (
                      <tr className={styles.channelRowSeparator}>
                        <td colSpan={8}></td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          </Spin>
        </Card.Body>
        </Card>
      </Form>
    </Container>
      </div>
  );
};

export default withAuthenticationRequired(ChannelDashboard);
